import { useState, useEffect } from 'react';
import './user-perfil.css';
import { put, getBlob, post, remove } from "../../services/api";
import { useUI } from '../../UIContext';
import { formatPhonePt, handleErr } from '../../utils/functions';
import Input from '../Input';
import PhoneInput from '../PhoneInput'
import PhotoInput from '../PhotoInput';
import FormModal from '../FormModal';
import { useUser } from '../../UserContext';

function UserPerfil({ showModal, onClose }) {
    const { setLoading, setMessage } = useUI();
    const { user: currentUser, setUser: setCurrentUser } = useUser();
    const [user, setUser] = useState({});
    const [photo, setPhoto] = useState(undefined);
    const [submitAttempt, setSubmitAttempt] = useState(false);

    useEffect(() => {
        if (!showModal || !currentUser) {
            return;
        }

        const name = currentUser.name || '';
        const lastName = currentUser.lastName || '';
        const photoPath = currentUser.photoPath || null;

        setUser({
            name,
            lastName,
            initials: `${name[0] ?? ''}${lastName[0] ?? ''}`.toUpperCase(),
            phone: formatPhonePt(currentUser?.person?.phone || currentUser?.phone || ''),
            photo: {
                path: photoPath,
                changed: false,
            }
        });

        if (!photoPath) {
            setPhoto(undefined);
            return;
        }

        async function getUserPhoto() {
            setLoading(true);

            await getBlob('/user/photo', true, setMessage,
                (response) => {
                    setPhoto(response.data);
                },
                (err) => {
                    handleErr(setMessage, `Falha ao carregar a foto do utilizador: ${err}`);
                },
                () => {
                    setLoading(false);
                }
            );
        }

        getUserPhoto();
    }, [showModal, currentUser, setLoading, setMessage]);

    if (!showModal)
        return null;

    function isNameValid() {
        return !!user.name && user.name.length > 2;
    }

    function isLastNameValid() {
        return !!user.lastName && user.lastName.length > 2;
    }

    function isValid() {
        setSubmitAttempt(true);

        return !!isNameValid() && !!isLastNameValid();
    }

    async function addPhoto() {
        const formData = new FormData();
        formData.append('photo', photo)

        await post('/user/photo', formData, true, setMessage,
            () => {},
            (err) => {
                handleErr(setMessage, `Falha ao salvar os dados do utilizador: ${err}`);
            },
            () => {
                setLoading(false);
            }
        );
    }

    async function removePhoto() {
        await remove('/user/photo', true, setMessage,
            () => {},
            (err) => {
                handleErr(setMessage, `Falha ao salvar os dados do utilizador: ${err}`);
            },
            () => {
                setLoading(false);
            }
        );
    }

    function showSuccess(updatedUser) {
        setCurrentUser(updatedUser);
        onClose();
        setMessage({ type: 'success', text: 'Os seus dados foram atualizados com sucesso!' });
    }

    async function onClickSavePerfil(e) {
        e.preventDefault();
    
        if (!isValid())
            return;

        setLoading(true);

        const data = {
            name: user.name,
            lastName: user.lastName,
            phone: user.phone,
        };

        const hasPhotoUploaded = !!user.photo.changed;
        const hasPhotoRemoved = !!user.photo.path && !photo;
        const hasPhotoChanged = hasPhotoUploaded || hasPhotoRemoved;

        await put('/user', data, true, setMessage,
            async () => {
                if (hasPhotoUploaded)
                    await addPhoto();
                else if (hasPhotoRemoved)
                    await removePhoto();

                showSuccess({
                    ...currentUser,
                    name: user.name,
                    lastName: user.lastName,
                    phone: user.phone,
                    person: {
                        ...(currentUser?.person || {}),
                        phone: user.phone,
                    },
                    photoPath: photo ? (currentUser?.photoPath || 'user-photo') : null,
                    photoVersion: hasPhotoChanged
                        ? Date.now()
                        : currentUser?.photoVersion,
                });
            },
            (err) => {
                handleErr(setMessage, `Falha ao salvar os dados do utilizador: ${err}`);
            },
            () => {
                if (!photo)
                    setLoading(false);
            }
        );
    }

    function onChangePhotoUpload(file) {
        setPhoto(file);
        setUser({
            ...user,
            photo: {
                ...user.photo,
                changed: true
            }
        });
    }

    function onClickCloseUserPerfil() {
        setUser({});
        setPhoto(undefined);
        setSubmitAttempt(false);

        onClose();
    }

    function onRemovePhoto() {
        setPhoto(undefined);
        setUser({
            ...user,
            photo: {
                ...user.photo,
                changed: false
            }
        });
    }

    return (
        <FormModal
            showModal={showModal}
            onClose={onClickCloseUserPerfil}>
            <form id="user-form">
                <section id="user-inputs">
                    <section id="photo">
                        <PhotoInput
                            initials={user.initials}
                            photo={photo}
                            onChange={onChangePhotoUpload}
                            onRemove={onRemovePhoto}
                        />
                    </section>

                    <hr className='vertical' />

                    <section id="user-data">
                        <Input
                            type='text'
                            className='glass-effect-15'
                            placeholder='Nome'
                            value={user.name}
                            onChange={(e) => setUser({ ...user, name: e.target.value })}
                            onValidate={isNameValid}
                            warningMsg='O nome do utilizador deve conter ao menos 3 caracteres.'
                            maxLength={56}
                            shouldValidate={submitAttempt}
                        />

                        <Input
                            type='text'
                            className='glass-effect-15'
                            placeholder='Sobrenome'
                            value={user.lastName}
                            onChange={(e) => setUser({ ...user, lastName: e.target.value })}
                            onValidate={isLastNameValid}
                            warningMsg='O sobrenome do utilizador deve conter ao menos 3 caracteres.'
                            maxLength={200}
                            shouldValidate={submitAttempt}
                        />

                        <PhoneInput
                            className='glass-effect-15'
                            placeholder='Telefone'
                            value={user.phone}
                            onChange={(e) => setUser({ ...user, phone: e.target.value })}
                        />
                    </section>
                </section>

                <section id="user-perfil-buttons">
                    <input
                        type='submit'
                        id='save-user-perfil'
                        className='user-form-button blue-button zoom-effect'
                        value="Salvar"
                        onClick={onClickSavePerfil}
                    />
                </section>
            </form>
        </FormModal>
    );
}

export default UserPerfil;